import React from "react";
import { motion } from "framer-motion";

const stack = [
  { name: "Personal Agent Site", category: "Web", desc: "Every agent gets their own branded IDX website — lead capture, listings, and blog included." },
  { name: "Full Agent CRM", category: "Lead Management", desc: "Complete CRM with pipelines, drip campaigns, and AI-scored lead routing. No add-on fees." },
  { name: "Automated Social Posts", category: "Marketing", desc: "Auto-generated social content — new listings, price drops, sold announcements — posted for you." },
  { name: "Marketplace Integrations", category: "Data", desc: "Full integration with Zillow, Realtor.com, Redfin, Homes.com and 50+ MLS boards nationwide." },
  { name: "Transaction Suite", category: "Compliance", desc: "End-to-end e-sign, TC workflow, and broker-approved compliance audit built in." },
];

const SOFTWARE_IMG_1 =
  "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxjcm0lMjBkYXNoYm9hcmQlMjBzb2Z0d2FyZXxlbnwwfHx8fDE3NzgxODc4NzN8MA&ixlib=rb-4.1.0&q=85";
const SOFTWARE_IMG_2 =
  "https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxjcm0lMjBkYXNoYm9hcmQlMjBzb2Z0d2FyZXxlbnwwfHx8fDE3NzgxODc4NzN8MA&ixlib=rb-4.1.0&q=85";

export default function SoftwareStack() {
  return (
    <section
      id="software"
      data-testid="software-section"
      className="relative py-24 sm:py-32 lg:py-40 border-t border-black/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-20"
        >
          <div className="overline mb-4">The Stack</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0a0a0a]">
            A <em className="not-italic text-[#121275]">$24,000/yr</em> tech stack.{" "}
            <span className="text-black/60">Yours. Free.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative">
          {/* Floating dashboard images */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 relative h-[420px]"
          >
            <div
              className="absolute top-0 left-0 w-[78%] h-[68%] bg-cover bg-center border border-black/15 shadow-[0_20px_80px_rgba(0,0,0,0.7)]"
              style={{ backgroundImage: `url(${SOFTWARE_IMG_1})` }}
            />
            <div
              className="absolute bottom-0 right-0 w-[60%] h-[58%] bg-cover bg-center border border-[#121275]/30 shadow-[0_20px_80px_rgba(212,175,55,0.18)]"
              style={{ backgroundImage: `url(${SOFTWARE_IMG_2})` }}
            />
            <div className="absolute -bottom-3 -left-3 numeric text-xs text-black/40 tracking-widest uppercase">
              1stCRM v4 / Live preview
            </div>
          </motion.div>

          <div className="lg:col-span-5 space-y-3">
            {stack.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`software-item-${i}`}
                className="group flex items-baseline gap-6 border-b border-black/10 py-4 hover:border-[#121275]/40 transition-colors"
              >
                <span className="numeric text-xs text-[#121275] tabular-nums">
                  0{i + 1}
                </span>
                <div className="flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display text-xl text-[#0a0a0a] font-light">{s.name}</h3>
                    <span className="numeric text-[10px] uppercase tracking-widest text-black/40">
                      {s.category}
                    </span>
                  </div>
                  <p className="text-sm text-black/55 mt-1.5">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
